import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TabItem } from '@common_components/buttons/Tabs.component';
import { useDispatch, useSelector } from 'react-redux';
import { dayjs, RootState } from '@umanforge/common';
import { useEquipment } from './equipment.hook';
import { TaskAction } from 'interfaces/taskAction.interface';
import { RoleTaskFull } from 'interfaces/roleTask.interface';
import { TaskFull } from 'interfaces/task.interface';
import { TaskService } from 'services/task.service';
import { RoleTaskService } from 'services/roleTask.service';
import { LocationService } from 'services/location.service';
import { TaskActionService } from 'services/taskAction.service';
import { Location } from 'interfaces/location.interface';
import { GlobalService } from 'services/global.service';
import { useRoleTaskForm } from 'features/role_task/hook/roleTaskForm.hook';
import { Recurrence } from 'interfaces/recurrence.interface';
import { RecurrenceService } from 'services/recurrence.service';

export function useTaskModalInfo( taskId: string, sectorId: string ) {
  const { t } = useTranslation();
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const dispatch = useDispatch();
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  const [ isAdmin, setIsAdmin ] = useState( false );

  // Form fields
  const [ selectedLocation, setSelectedLocation ] = useState<Location>( );
  const [ selectedTaskAction, setSelectedTaskAction ] = useState<TaskAction>( );
  const [ dateStart, setTimeStart ] = useState<dayjs.Dayjs | undefined>();
  const [ dateEnd, setTimeEnd ] = useState<dayjs.Dayjs | undefined>();
  const [ fullDay, setFullDay ] = useState( false );
  const [ recurrence, setRecurrence ] = useState<Recurrence>();


  // Modal
  const [ activeTab, setActiveTab ] = useState( 0 );
  const [ isFirstRender, setIsFirstRender ] = useState( true );

  // External data
  const [ task, setTask ] = useState<TaskFull>( );

  const defaultRoleTask: RoleTaskFull = {
    abilities: [],
    skills: [],
    users: [],
    id: '',
    idTask: '',
    count: 1
  };
  const [ roleTasks, setRoleTasks ] = useState<RoleTaskFull[]>( taskId ? [] : [ { ...defaultRoleTask } ] );
  const { selectedEquipments,  } = useEquipment( sectorId, taskId );


  const {
    handleAddRoleTask,
    handleRoleTaskDelete,
    handleRoleTaskChange,
  } = useRoleTaskForm(
    sectorId,
    undefined,
    () => { /** */},
    [],
    [],
  );

  useEffect( () => {
    setIsFirstRender( false );
  }, [ ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      if ( me ) {
        const admin = GlobalService.isAdminOfSector( me, sectorId );
        setIsAdmin( admin );
      }
    }
  }, [ isFirstRender ] );

  const pages: TabItem[] = useMemo( () => {
    const tabs: TabItem[] = [
      { id: 'actions', name: t( 'task' ) }
    ];
    if ( task ) {
      tabs.push( { id: 'roletasks', name: t( 'attribution' ) } );
    }
    tabs.push( { id: 'task_info', name: t( 'details' ) } );
    return tabs;
  }, [ task, t ] );

  // Use Effects
  useEffect( () => {
    if ( !isFirstRender ) {
      /* */
    }
  }, [ isFirstRender ] );

  useEffect( () => {
    init();
  }, [ ] );


  const init = async () => {
    try {
      if ( taskId ) {
        const [
          taskRes,
          roleTasksRes
        ] = await Promise.all( [
          TaskService.getTaskFullById( taskId, token, dispatch ),
          RoleTaskService.getRoleTasksFullByTaskId( taskId, token, dispatch )
        ] );

        if ( taskRes ) {
          setTask( taskRes );
          setTimeStart( dayjs( taskRes.dateStart ) );
          setTimeEnd( dayjs( taskRes.dateEnd ) );
          setFullDay( ( dayjs( taskRes.dateStart ).format( 'HH:mm' ) === '00:00' && dayjs( taskRes.dateEnd ).format( 'HH:mm' ) === '23:59' ) );

          const [
            locationRes,
            taskActionRes
          ] = await Promise.all( [
            LocationService.getLocationById( taskRes.location.id, token, dispatch ),
            TaskActionService.getTaskActionById( taskRes.taskAction.id, token, dispatch )
          ] );

          setSelectedLocation( locationRes );
          setSelectedTaskAction( taskActionRes  );

          if ( taskRes?.idRecurrence ) {
            const recurrenceRes = await RecurrenceService.getRecurrenceById( taskRes.idRecurrence, token, dispatch );
            if ( recurrenceRes ) {
              setRecurrence( recurrenceRes );
            }
          }
        }

        if ( roleTasksRes ) {
          setRoleTasks( roleTasksRes );
        }
      }
    }
    catch ( error ) {
      console.error( 'Error fetching task:', error );
    }
    setIsFirstRender( false );
  };

  return {
    selectedLocation,
    selectedTaskAction,
    selectedEquipments,
    dateStart,
    dateEnd,
    roleTasks,
    activeTab, setActiveTab,
    task,
    pages,
    isAdmin,
    fullDay, setFullDay,
    recurrence,

    handleAddRoleTask,
    handleRoleTaskDelete,
    handleRoleTaskChange,
  };
};

export default useTaskModalInfo;
